/**
 * `canvas_node_action` — a user node mutation committed (RFC-1577 §6).
 *
 * ## Where it fires
 *
 * At the commit, not at the button: {@link useNodeActionAnalytics} observes
 * the canvas YDoc's `nodes` map and logs once a node key has been added or
 * deleted in a transaction. Moves, resizes and data edits update an existing
 * key and are never logged.
 *
 * ## User actions only
 *
 * Two filters, both required:
 *
 * 1. `transaction.local` — the change was made by this browser. Director /
 *    agent mutations (`propose_dag_changes` → `GraphManager.apply_proposal`)
 *    are written server-side and reach the client as remote Yjs updates, so
 *    they are never local. Collaborators' edits are remote too.
 * 2. A user intent for that node id, noted by the UI entry point with
 *    {@link noteNodeActions} (add menu, asset library, upload, drop, paste,
 *    duplicate, delete confirmation). Local commits without an intent —
 *    undo/redo restores, landing-page upload seeding, programmatic side
 *    effects such as reference-library imports — are not user add/delete
 *    actions and are dropped. The intent also supplies the `source`.
 *
 * Intents are matched by node id, so an entry point may note before an async
 * commit (Add node resolves a video model first) or straight after a
 * synchronous one (toolbar Duplicate learns the copy's id from the return
 * value): commits are resolved on a microtask, after the gesture's own code.
 */

import { useEffect } from 'react'
import * as Y from 'yjs'
import type { RawCanvasYNode, RawCanvasYNodeTable } from '../hooks/useYCanvas'
import { createNadDebug } from '../utils/debugLog'
import { isRenderableCanvasNodeType } from '../utils/nodeTypes'
import { track } from './events'
import type { NodeMutationAction, NodeMutationSource } from './generated/analyticsEvents'
import { analyticsNodeType } from './nodeAnalytics'

const log = createNadDebug('dw:analytics-events:node-action')

/** An intent is stale after this long without a matching commit. */
const INTENT_TTL_MS = 30_000

interface NodeActionIntent {
  action: NodeMutationAction
  source: NodeMutationSource
  notedAt: number
}

const intents = new Map<string, NodeActionIntent>()

function pruneIntents(now: number): void {
  intents.forEach((intent, nodeId) => {
    if (now - intent.notedAt > INTENT_TTL_MS) intents.delete(nodeId)
  })
}

/**
 * Record that the user asked for `action` on these node ids, from `source`.
 * Nothing is logged until the matching YDoc commit is observed.
 */
export function noteNodeActions(
  nodeIds: readonly string[],
  action: NodeMutationAction,
  source: NodeMutationSource,
): void {
  const now = Date.now()
  pruneIntents(now)
  for (const nodeId of nodeIds) intents.set(nodeId, { action, source, notedAt: now })
}

/** @internal test seam */
export function __clearNodeActionIntents(): void {
  intents.clear()
}

type CommitKind = 'add' | 'delete'

function takeIntent(nodeId: string, kind: CommitKind, now: number): NodeActionIntent | null {
  const intent = intents.get(nodeId)
  if (!intent) return null
  intents.delete(nodeId)
  if (now - intent.notedAt > INTENT_TTL_MS) return null
  const matches = kind === 'delete' ? intent.action === 'delete' : intent.action === 'add' || intent.action === 'duplicate'
  return matches ? intent : null
}

// Raw node rows come through the Yjs boundary's exported row types
// (`RawCanvasYNode`), never the raw YDoc value type.
function nodeTypeOf(value: RawCanvasYNode | undefined): string | undefined {
  if (!(value instanceof Y.Map)) return undefined
  const type = value.get('type')
  return typeof type === 'string' ? type : undefined
}

function countVisibleNodes(yNodes: RawCanvasYNodeTable): number {
  let count = 0
  yNodes.forEach((value) => {
    const type = nodeTypeOf(value)
    if (type !== undefined && isRenderableCanvasNodeType(type)) count += 1
  })
  return count
}

interface CommittedChange {
  nodeId: string
  kind: CommitKind
  nodeType: string
  countAfter: number
}

/**
 * Observe `ydoc`'s nodes map and emit `canvas_node_action` for committed,
 * user-intended adds/deletes. Mount once per canvas (WorkspacePageV2).
 */
export function useNodeActionAnalytics(ydoc: Y.Doc | null | undefined, projectId: string | undefined): void {
  useEffect(() => {
    if (!ydoc || !projectId) return
    const yNodes: RawCanvasYNodeTable = ydoc.getMap<RawCanvasYNode>('nodes')
    // A deleted node's Y.Map can no longer be read, so remember every type.
    const typeById = new Map<string, string>()
    yNodes.forEach((value, nodeId) => {
      const type = nodeTypeOf(value)
      if (type !== undefined) typeById.set(nodeId, type)
    })

    let pending: CommittedChange[] = []
    let flushQueued = false
    const flush = () => {
      flushQueued = false
      const batch = pending
      pending = []
      const now = Date.now()
      for (const change of batch) {
        const intent = takeIntent(change.nodeId, change.kind, now)
        if (!intent) {
          log({ event: 'node-action-skip', reason: 'no-user-intent', node_id: change.nodeId, kind: change.kind })
          continue
        }
        try {
          track('canvas_node_action', {
            project_id: projectId,
            node_id: change.nodeId,
            node_type: analyticsNodeType(change.nodeType),
            action: intent.action,
            source: intent.source,
            node_count_after: change.countAfter,
          })
        } catch (err) {
          log({ event: 'node-action-track-failed', node_id: change.nodeId, error: String(err) })
        }
      }
    }

    const observer = (event: Y.YMapEvent<RawCanvasYNode>) => {
      try {
        const local = event.transaction.local
        let countAfter: number | null = null
        event.changes.keys.forEach((change, nodeId) => {
          let kind: CommitKind
          let nodeType: string | undefined
          if (change.action === 'add') {
            kind = 'add'
            nodeType = nodeTypeOf(yNodes.get(nodeId))
            if (nodeType !== undefined) typeById.set(nodeId, nodeType)
          } else if (change.action === 'delete') {
            kind = 'delete'
            nodeType = typeById.get(nodeId)
            typeById.delete(nodeId)
          } else {
            return
          }
          if (!local || nodeType === undefined) return
          if (countAfter === null) countAfter = countVisibleNodes(yNodes)
          pending.push({ nodeId, kind, nodeType, countAfter })
        })
        if (pending.length > 0 && !flushQueued) {
          flushQueued = true
          queueMicrotask(flush)
        }
      } catch (err) {
        log({ event: 'node-action-observe-failed', error: String(err) })
      }
    }

    yNodes.observe(observer)
    return () => {
      yNodes.unobserve(observer)
    }
  }, [ydoc, projectId])
}
